"use client";

import { motion } from "framer-motion";
import { FaLocationArrow } from "react-icons/fa6";

import { projects } from "@/data";
import { usePortfolioProjects } from "@/hooks/usePortfolioProjects";
import { PinContainer } from "./ui/Pin";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const RecentProjects = () => {
  const allProjects = usePortfolioProjects(projects);

  return (
    <section id="projects" className="py-20">
      <div>
        <motion.h2
          className="heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          Featured <span className="text-purple">Projects</span>
        </motion.h2>
        <motion.p
          className="text-white-200 text-center mt-4 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
        >
          A collection of selected projects focused on performance, clean UI,
          and modern product experiences.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center px-2 sm:px-4 gap-2 sm:gap-4 md:gap-6 lg:gap-8 xl:gap-16 mt-10 w-full"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          {allProjects.map((item) => {
            const href = item.link.startsWith("http")
              ? item.link
              : `https://${item.link.replace(/^\/+/, "")}`;
            const linkLabel = href
              .replace(/^https?:\/\//, "")
              .replace(/\/$/, "");

            return (
              <motion.div
                className="interactive-lift lg:min-h-[32.5rem] h-[25rem] flex items-center justify-center w-full min-w-0 max-w-[90vw] sm:max-w-[400px] md:max-w-[420px] lg:max-w-[360px] xl:max-w-[384px]"
                key={String(item.id)}
                variants={itemVariants}
              >
                <PinContainer title={linkLabel} href={href}>
                  <div className="relative flex items-center justify-center w-full overflow-hidden h-[20vh] lg:h-[30vh] mb-10">
                    <div
                      className="relative w-full h-full overflow-hidden lg:rounded-3xl"
                      style={{ backgroundColor: "#13162D" }}
                    >
                      <img
                        src="/bg.png"
                        alt="Project showcase background pattern"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <img
                      src={item.img}
                      alt={`${item.title} project showcase`}
                      loading="lazy"
                      decoding="async"
                      className="z-10 absolute bottom-0"
                    />
                  </div>

                  <h3 className="font-bold lg:text-2xl md:text-xl text-base line-clamp-1">
                    {item.title}
                  </h3>
                  {item.source === "admin" && (
                    <p className="mt-2 text-xs uppercase tracking-[0.14em] text-blue-100">
                      Admin Upload
                    </p>
                  )}

                  <p
                    className="lg:text-xl lg:font-normal font-light text-sm line-clamp-2"
                    style={{
                      color: "#BEC1DD",
                      margin: "1vh 0",
                    }}
                  >
                    {item.des}
                  </p>

                  <div className="flex items-center justify-between mt-7 mb-3">
                    <div className="flex items-center">
                      {item.iconLists.length > 0 ? (
                        item.iconLists.map((icon, index) => (
                          <div
                            key={index}
                            className="border border-white/[.2] rounded-full bg-black lg:w-10 lg:h-10 w-8 h-8 flex justify-center items-center"
                            style={{
                              transform: `translateX(-${5 * index + 2}px)`,
                            }}
                          >
                            <img
                              src={icon}
                              alt={`${item.title} tech stack icon`}
                              className="p-2"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-white-200">
                          Tech stack not added
                        </p>
                      )}
                    </div>

                    <div className="flex justify-center items-center">
                      <p className="flex lg:text-xl md:text-xs text-sm text-purple">
                        Check Live Site
                      </p>
                      <FaLocationArrow className="ms-3" color="#CBACF9" />
                    </div>
                  </div>
                </PinContainer>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default RecentProjects;
